import React, { useState } from "react";
import {
  FileText,
  Link,
  CloudUpload,
  Globe,
  Sparkles,
  Trash2,
} from "lucide-react";
import useStrategy from "../../../hooks/useStrategy";
import { BrandDetails, KnowledgeSource } from "../../../types/add-brand-types";
import "./strategy.css";
import Toast from "../../uiComponents/Toast";
import Modal from "../../uiComponents/Modal";
import Markdown from "react-markdown";

interface StrategyProps {
  brandData: BrandDetails;
  refreshBrandDetails: () => void | Promise<void>;
  onOpenCreatorStudio?: () => void;
}

const Strategy: React.FC<StrategyProps> = ({
  brandData,
  refreshBrandDetails,
  onOpenCreatorStudio,
}) => {
  const {
    loading,
    toast,
    setToast,
    setInputValue,
    inputValue,
    sourceName,
    setSourceName,
    fileInputRef,
    handleFileChange,
    productSummary,
    websiteContextDisplay,
    addUrlKnowledgeSource,
    removeKnowledgeSource,
    plan,
    knowledgeSourceLimitReached,
    progressState,
  } = useStrategy(brandData, refreshBrandDetails);
  const shouldDisableKnowledgeSourceActions =
    loading || knowledgeSourceLimitReached;

  // const [isTopicsExpanded, setIsTopicsExpanded] = useState(false);
  const [showProductSummaryModal, setShowProductSummaryModal] = useState(false);
  const [selectedKnowledgeSource, setSelectedKnowledgeSource] = useState<{
    name: string;
    source: KnowledgeSource;
  } | null>(null);
  const [pendingDeleteKey, setPendingDeleteKey] = useState<string | null>(null);

  // const displayedTopics = useMemo(() => {
  //   if (!detectedTopics) {
  //     return [];
  //   }
  //   return isTopicsExpanded ? detectedTopics : detectedTopics.slice(0, 15);
  // }, [isTopicsExpanded, detectedTopics]);

  // const remainingTopicsCount = useMemo(() => {
  //   if (!detectedTopics) {
  //     return 0;
  //   }
  //   return detectedTopics.length - 15;
  // }, [detectedTopics]);

  return (
    <>
      <div className="strategy-content">
        {loading && (
          <div className="strategy-loading-overlay">
            <div className="strategy-progress-card">
              <div className="spinner"></div>
              <div className="strategy-progress-title">
                {progressState?.title ?? "Updating brand knowledge"}
              </div>
              {progressState ? (
                <div className="strategy-progress-steps">
                  {progressState.steps.map((step, index) => (
                    <div
                      key={step}
                      className={`strategy-progress-step ${
                        index < progressState.activeIndex
                          ? "strategy-progress-step--complete"
                          : ""
                      } ${
                        index === progressState.activeIndex
                          ? "strategy-progress-step--active"
                          : ""
                      }`}
                    >
                      <span className="strategy-progress-dot" />
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              {progressState?.helperText ? (
                <div className="strategy-progress-helper">
                  {progressState.helperText}
                </div>
              ) : null}
            </div>
          </div>
        )}
        {/* Left Column: Train Your Agent */}
        <div className="train-column">
          <div>
            <div className="section-title">Train Your Agent</div>
            <div className="section-subtitle">
              Upload documents or links to help the AI understand your product.
            </div>
            <div className="website-context-status-card">
              <div className="website-context-status-header">
                <span className="website-context-status-title">
                  Website context
                </span>
                <span
                  className={`website-context-status-badge website-context-status-badge--${websiteContextDisplay.tone}`}
                >
                  {websiteContextDisplay.label}
                </span>
              </div>
              <div className="website-context-status-copy">
                {websiteContextDisplay.state === "basic_context" &&
                  "Drafts currently rely on your brand setup until website context finishes processing."}
                {websiteContextDisplay.state === "processing" &&
                  "We are crawling and summarizing the site in the background."}
                {websiteContextDisplay.state === "ready" &&
                  "Your website context is ready and will be used in downstream generation."}
                {websiteContextDisplay.state === "failed" &&
                  "Website processing failed. You can still add the site manually below."}
                {websiteContextDisplay.state === "timed_out" &&
                  "Website processing took too long. You can retry by adding the site again below."}
              </div>
              {websiteContextDisplay.sourceUrl ? (
                <div className="website-context-status-meta">
                  Source: {websiteContextDisplay.sourceUrl}
                </div>
              ) : null}
              {websiteContextDisplay.error ? (
                <div className="website-context-status-error">
                  {websiteContextDisplay.error}
                </div>
              ) : null}
            </div>
            {knowledgeSourceLimitReached && !plan?.betaEnabled && (
              <div
                style={{
                  marginTop: "12px",
                  marginBottom: "16px",
                  padding: "12px 14px 18px",
                  borderRadius: "12px",
                  background: "#fff7ed",
                  border: "1px solid #fdba74",
                  color: "#9a3412",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Free plan includes 1 knowledge source. Upgrade to add
                more sources.
              </div>
            )}

            <div
              className="url-input-container"
              style={{ flexDirection: "column", gap: "0" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <span style={{ padding: "0 12px 0 4px", color: "#9ca3af" }}>
                  <FileText size={18} />
                </span>
                <input
                  type="text"
                  className="url-input"
                  style={{ fontSize: "14px", fontWeight: 500 }}
                  placeholder="Name for this source (e.g., Homepage, Blog)..."
                  value={sourceName}
                  onChange={(e) => setSourceName(e.target.value)}
                  disabled={shouldDisableKnowledgeSourceActions}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px",
                  gap: "8px",
                }}
              >
                <span style={{ padding: "0 4px", color: "#9ca3af" }}>
                  <Link size={18} />
                </span>
                <input
                  type="text"
                  className="url-input"
                  style={{ fontSize: "14px" }}
                  placeholder="Paste website or article URL..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  disabled={shouldDisableKnowledgeSourceActions}
                />
                <button
                  className="add-btn"
                  style={{
                    alignSelf: "center",
                    padding: "8px 20px",
                    backgroundColor: shouldDisableKnowledgeSourceActions
                      ? "#f3f4f6"
                      : "#4f46e5",
                    color: shouldDisableKnowledgeSourceActions
                      ? "#9ca3af"
                      : "white",
                    border: "none",
                    boxShadow: shouldDisableKnowledgeSourceActions
                      ? "none"
                      : "0 2px 4px rgba(79, 70, 229, 0.2)",
                    fontWeight: 600,
                    transition: "all 0.2s ease",
                    cursor: shouldDisableKnowledgeSourceActions
                      ? "not-allowed"
                      : "pointer",
                  }}
                  onClick={() => {
                    if (!sourceName.trim()) {
                      setToast({
                        message: "Please enter a name for this source.",
                        type: "warning",
                        isVisible: true,
                      });
                      setTimeout(() => setToast(null), 3000);
                      return;
                    }
                    addUrlKnowledgeSource(inputValue, brandData.id);
                  }}
                  disabled={shouldDisableKnowledgeSourceActions}
                >
                  {loading ? "Adding..." : "Add"}
                </button>
              </div>
            </div>

            <div className="divider-or">OR</div>

            <div
              className="drop-zone"
              onClick={() =>
                !shouldDisableKnowledgeSourceActions && fileInputRef.current?.click()
              }
              style={{
                cursor: shouldDisableKnowledgeSourceActions ? "not-allowed" : "pointer",
                opacity: shouldDisableKnowledgeSourceActions ? 0.6 : 1,
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept=".pdf,.docx,.txt"
              />
              <div className="upload-icon-circle">
                <CloudUpload size={24} />
              </div>
              <div style={{ textAlign: "center" }}>
                <div className="drop-text-primary">
                  {shouldDisableKnowledgeSourceActions
                    ? "Upgrade to add more sources"
                    : "Drop PDF guide or Upload"}
                </div>
                <div className="drop-text-secondary">
                  {shouldDisableKnowledgeSourceActions
                    ? "Paid plans unlock more knowledge sources"
                    : "PDF, DOCX, or TXT files supported"}
                </div>
              </div>
            </div>
          </div>

          <div className="active-sources">
            <div
              className="section-subtitle"
              style={{
                fontSize: "12px",
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
            >
              ACTIVE SOURCES
            </div>
            <div className="kb-source-list">
              {Object.keys(brandData.knowledge_base || {}).length === 0 ? (
                <div className="kb-source-empty">
                  <FileText size={18} />
                  <span>
                    No active sources yet. Add a URL or upload a file.
                  </span>
                </div>
              ) : (
                Object.entries(brandData.knowledge_base)
                  .reverse()
                  .map(([key, source]) => (
                    <div
                      className="kb-source-card"
                      key={key}
                      onClick={() =>
                        setSelectedKnowledgeSource({ name: key, source })
                      }
                    >
                      <div className="kb-source-info">
                        <div className="kb-source-icon">
                          <Globe size={18} />
                        </div>
                        <div className="kb-source-details">
                          <div
                            className="kb-source-name"
                            title={source.actual_name || key}
                          >
                            {source.actual_name || key}
                          </div>
                          <div className="kb-source-type">
                            {source.type?.startsWith("http")
                              ? "Website Crawl"
                              : "From Document"}
                          </div>
                        </div>
                      </div>
                      <div className="kb-source-badge">{"SYNCED"}</div>
                      <button
                        className="kb-source-delete-btn"
                        title="Remove source"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDeleteKey(key);
                        }}
                        disabled={loading}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Strategic Analysis */}
        <div className="analysis-column">
          <div className="section-title">
            <Sparkles
              size={20}
              style={{ verticalAlign: "middle", marginRight: "6px" }}
            />
            Market Intelligence
          </div>
          {plan && !plan.features.seo && !plan.betaEnabled && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px 14px",
                borderRadius: "12px",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                color: "#1d4ed8",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              SEO article generation is available on Pro. Free users can still
              train the agent with one knowledge source.
            </div>
          )}

          <div className="analysis-card">
            <div className="card-header">
              <div className="card-title">Product Summary</div>
            </div>
            <div
              className="opp-text product-summary-preview"
              style={{ marginTop: 0 }}
            >
              {productSummary ||
                "Upload websites or documents to generate a concise product summary."}
            </div>
            {productSummary && (
              <button
                className="product-summary-view-btn"
                onClick={() => setShowProductSummaryModal(true)}
              >
                View
              </button>
            )}
          </div>

          {/* Detected Topics - temporarily hidden
          <div className="analysis-card">
            <div className="card-header">
              <div className="card-title"># Detected Topics</div>
              <span
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  background: "#f3f4f6",
                  padding: "4px 8px",
                  borderRadius: "12px",
                }}
              >
                From {brandData.knowledge_sources.length} sources
              </span>
            </div>
            <div className="topics-container">
              {detectedTopics.length === 0 ? (
                <div style={{ color: "#6b7280", fontStyle: "italic" }}>
                  Upload your website or PDF to add knowledge
                </div>
              ) : (
                <>
                  {displayedTopics.map((topic, i) => (
                    <div
                      key={i}
                      className={`topic-tag ${
                        ["IoT", "Cost Savings"].includes(topic)
                          ? "highlight"
                          : ""
                      }`}
                    >
                      {topic}
                    </div>
                  ))}
                  {!isTopicsExpanded && remainingTopicsCount > 0 && (
                    <div
                      className="topic-tag expand-tag"
                      onClick={() => setIsTopicsExpanded(true)}
                    >
                      +{remainingTopicsCount} more
                    </div>
                  )}
                  {isTopicsExpanded && detectedTopics.length > 15 && (
                    <div
                      className="topic-tag collapse-tag"
                      onClick={() => setIsTopicsExpanded(false)}
                    >
                      Show less
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          */}

          {/* Content Opportunity - temporarily hidden
          {knowledgeAddResponse?.contentOpportunity && (
            <div className="analysis-card content-opportunity-card">
              <div
                className="card-title"
                style={{
                  color: "#4f46e5",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <TrendingUp size={18} />
                Content Opportunity
              </div>
              <div className="opp-text">
                {knowledgeAddResponse?.contentOpportunity}
              </div>
              <button className="primary-btn">Draft Post on This</button>
            </div>
          )}
          */}

          {/* Tone Consistency - temporarily hidden
          {knowledgeAddResponse?.toneConsistency && (
            <div className="analysis-card tone-card">
              <div
                className="card-title"
                style={{
                  color: "#059669",
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Check size={18} />
                Tone Consistency
              </div>
              <div className="opp-text" style={{ margin: 0 }}>
                {knowledgeAddResponse?.toneConsistency}
              </div>
            </div>
          )}
          */}
        </div>
        <Modal
          isOpen={showProductSummaryModal}
          onClose={() => setShowProductSummaryModal(false)}
          title="Product Summary"
          size="medium"
          bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          <div className="product-summary-fulltext">
            {productSummary || "No product summary available."}
          </div>
        </Modal>

        <Modal
          isOpen={!!selectedKnowledgeSource}
          onClose={() => setSelectedKnowledgeSource(null)}
          title={
            selectedKnowledgeSource?.source.actual_name ||
            selectedKnowledgeSource?.name ||
            "Source Content"
          }
          size="medium"
          bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {selectedKnowledgeSource && (
            <div>
              <div className="knowledge-source-modal-meta">
                {selectedKnowledgeSource.source.type}
              </div>
              <div className="knowledge-source-modal-content">
                <Markdown>
                  {selectedKnowledgeSource.source.content ||
                    "No content available."}
                </Markdown>
              </div>
            </div>
          )}
        </Modal>

        <Modal
          isOpen={!!pendingDeleteKey}
          onClose={() => setPendingDeleteKey(null)}
          title="Remove Source"
          size="small"
        >
          <div style={{ color: "#374151", fontSize: "14px", lineHeight: 1.6 }}>
            Are you sure you want to remove{" "}
            <strong>
              {pendingDeleteKey &&
                (brandData.knowledge_base?.[pendingDeleteKey]?.actual_name ||
                  pendingDeleteKey)}
            </strong>
            ? This action cannot be undone.
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              marginTop: "20px",
            }}
          >
            <button
              className="kb-confirm-cancel-btn"
              onClick={() => setPendingDeleteKey(null)}
            >
              Cancel
            </button>
            <button
              className="kb-confirm-delete-btn"
              onClick={() => {
                if (pendingDeleteKey) {
                  removeKnowledgeSource(pendingDeleteKey, brandData.id);
                  setPendingDeleteKey(null);
                }
              }}
              disabled={loading}
            >
              {loading ? "Removing..." : "Remove"}
            </button>
          </div>
        </Modal>

        {toast && toast.isVisible && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
            duration={3000}
          />
        )}
      </div>
    </>
  );
};

export default Strategy;
